import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Target,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SEVERITY = {
  low: { tone: "bg-stone-100 text-stone-700 border-stone-200", icon: TrendingUp, label: "Baixa" },
  medium: { tone: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertTriangle, label: "Média" },
  high: { tone: "bg-orange-100 text-orange-900 border-orange-200", icon: AlertTriangle, label: "Alta" },
  critical: { tone: "bg-rose-100 text-rose-900 border-rose-200", icon: AlertTriangle, label: "Crítica" },
};

const PRIORITY_TONE = {
  low: "border-stone-200 text-stone-700",
  medium: "border-amber-200 text-amber-800",
  high: "border-orange-200 text-orange-800",
  critical: "border-rose-200 text-rose-800",
};

const PRIORITY_LABEL = {
  low: "Baixa",
  medium: "Média",
  high: "Alta",
  critical: "Crítica",
};

function riskColor(score) {
  if (score >= 70) return "text-rose-700";
  if (score >= 40) return "text-amber-700";
  if (score >= 15) return "text-yellow-700";
  return "text-emerald-700";
}

function RiskGauge({ score }) {
  const pct = Math.max(0, Math.min(100, score));
  const stroke = pct >= 70 ? "#be123c" : pct >= 40 ? "#d97706" : pct >= 15 ? "#ca8a04" : "#047857";
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" className="shrink-0">
      <circle cx="40" cy="40" r="34" fill="none" stroke="#e7e5e4" strokeWidth="8" />
      <circle
        cx="40"
        cy="40"
        r="34"
        fill="none"
        stroke={stroke}
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={2 * Math.PI * 34}
        strokeDashoffset={2 * Math.PI * 34 * (1 - pct / 100)}
        transform="rotate(-90 40 40)"
      />
      <text x="40" y="46" textAnchor="middle" className="font-display font-bold" fontSize="20" fill={stroke}>
        {pct}
      </text>
    </svg>
  );
}

export default function PredictiveInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai/predictive-insights?days=30");
      setData(data);
      setHasLoaded(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao gerar insights");
    } finally {
      setLoading(false);
    }
  };

  // Auto-load lazily after a few seconds (so dashboard renders quickly first)
  useEffect(() => {
    const t = setTimeout(run, 800);
    return () => clearTimeout(t);
  }, []);

  return (
    <section data-testid="predictive-insights" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 font-body">
            Análise de operação - IA
          </div>
          <h2 className="font-display text-xl font-bold tracking-tight mt-1">
            Padrões e ações recomendadas (últimos 30 dias)
          </h2>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={run}
          disabled={loading}
          className="border-emerald-200 text-emerald-800 hover:bg-emerald-50"
          data-testid="predictive-refresh"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4 mr-2" />
          )}
          {loading ? "Analisando…" : hasLoaded ? "Atualizar" : "Analisar agora"}
        </Button>
      </div>

      {loading && !data && (
        <Card className="p-6 rounded-xl border-stone-200 bg-white">
          <div className="h-[200px] flex items-center justify-center text-sm text-stone-400 font-body gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Ultra está analisando seus dados…
          </div>
        </Card>
      )}

      {data && (
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Risk score + summary */}
          <Card className="p-5 rounded-xl border-stone-200 bg-white">
            <div className="flex items-center gap-4">
              <RiskGauge score={data.risk_score || 0} />
              <div>
                <div className="text-[10px] font-body uppercase tracking-wider text-stone-500">
                  Índice de risco
                </div>
                <div className={`font-display text-2xl font-extrabold tracking-tight ${riskColor(data.risk_score || 0)}`}>
                  {data.risk_score >= 70
                    ? "Iminente"
                    : data.risk_score >= 40
                    ? "Atenção"
                    : data.risk_score >= 15
                    ? "Moderado"
                    : "Saudável"}
                </div>
              </div>
            </div>
            <p className="text-sm text-stone-700 font-body mt-4 leading-relaxed">
              {data.summary || "Sem padrões relevantes detectados."}
            </p>
          </Card>

          {/* Patterns */}
          <Card className="p-5 rounded-xl border-stone-200 bg-white">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-emerald-800" />
              <h3 className="font-display font-bold tracking-tight">Padrões recorrentes</h3>
            </div>
            <div className="space-y-3" data-testid="predictive-patterns">
              {(data.patterns || []).length === 0 && (
                <div className="text-sm text-stone-500 font-body py-4 text-center">
                  Nenhum padrão recorrente identificado.
                </div>
              )}
              {(data.patterns || []).map((p, i) => {
                const sev = SEVERITY[p.severity] || SEVERITY.low;
                return (
                  <div key={i} className={`border rounded-md px-3 py-2 ${sev.tone}`}>
                    <div className="flex items-start gap-2">
                      <sev.icon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold text-sm">{p.title}</div>
                        <div className="text-xs font-body opacity-80 mt-0.5">{p.evidence}</div>
                        {p.root_cause && (
                          <div className="text-[11px] font-body mt-1 italic opacity-75">
                            ▸ {p.root_cause}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Recommendations */}
          <Card className="p-5 rounded-xl border-stone-200 bg-white">
            <div className="flex items-center gap-2 mb-3">
              <Target className="h-4 w-4 text-emerald-800" />
              <h3 className="font-display font-bold tracking-tight">Ações recomendadas</h3>
            </div>
            <div className="space-y-3" data-testid="predictive-recommendations">
              {(data.recommendations || []).length === 0 && (
                <div className="text-sm text-stone-500 font-body py-4 text-center">
                  <CheckCircle2 className="h-5 w-5 mx-auto mb-2 text-emerald-700" />
                  Sem ações pendentes — operação ok!
                </div>
              )}
              {(data.recommendations || []).map((r, i) => (
                <div key={i} className="border-l-2 border-emerald-700 pl-3 py-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-display font-semibold text-sm leading-snug">{r.action}</div>
                    <Badge
                      variant="outline"
                      className={`text-[10px] shrink-0 ${PRIORITY_TONE[r.priority] || ""}`}
                    >
                      {PRIORITY_LABEL[r.priority] || r.priority || "—"}
                    </Badge>
                  </div>
                  {r.target && (
                    <div className="text-xs text-stone-500 font-body mt-0.5">
                      Para: <span className="font-semibold">{r.target}</span>
                    </div>
                  )}
                  {r.expected_impact && (
                    <div className="text-xs text-stone-600 font-body mt-0.5 italic">
                      → {r.expected_impact}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </section>
  );
}
